import type { CalEvent, WorkingHours } from "../types";
import { addDays, dayStart } from "./time";
import {
  allDayEvents,
  clashIds,
  eventVisual,
  findClashes,
  isActive,
  isFocus,
  needsReply,
  timedSegments,
  type EventVisual,
} from "./day";

/*
 * Week-view calculations, ported from the approved mockup's renderWeek and
 * applied to real events. Columns cover Monday to Friday; replies and the
 * weekend count look at the whole week.
 */

export interface WeekBar {
  readonly event: CalEvent;
  readonly startMin: number;
  readonly endMin: number;
  readonly visual: EventVisual;
}

export interface WeekDay {
  readonly day: string;
  readonly bars: readonly WeekBar[];
  /** Booked minutes: active meetings, not focus time. */
  readonly loadMin: number;
  readonly clashCount: number;
}

export interface WeekModel {
  readonly days: readonly WeekDay[];
  /** The bars' vertical range: the working day, widened to fit the week's meetings. */
  readonly range: { readonly startMin: number; readonly endMin: number };
  readonly stats: {
    readonly meetings: number;
    readonly bookedMin: number;
    readonly clashes: number;
    readonly toReply: number;
  };
  readonly firstClashDay: string | null;
  /** Invitations you haven't answered, earliest first; `clashes` = overlaps something you've accepted. */
  readonly awaiting: readonly { readonly event: CalEvent; readonly clashes: boolean }[];
  /** Active meetings on Saturday and Sunday. */
  readonly weekendCount: number;
}

export function weekModel(
  events: readonly CalEvent[],
  mon: string,
  acks: Readonly<Record<string, string>>,
  hours: WorkingHours,
): WeekModel {
  let startMin = hours.startMin;
  let endMin = hours.endMin;

  const columns = [0, 1, 2, 3, 4].map((i) => {
    const day = addDays(mon, i);
    const segs = timedSegments(events, day);
    const clashes = findClashes(segs, acks);
    for (const s of segs) {
      startMin = Math.min(startMin, Math.floor(s.startMin / 60) * 60);
      endMin = Math.max(endMin, Math.ceil(s.endMin / 60) * 60);
    }
    return { day, segs, clashes };
  });

  let meetings = 0;
  let bookedMin = 0;
  let clashTotal = 0;
  const days: WeekDay[] = columns.map(({ day, segs, clashes }) => {
    const clashing = clashIds(clashes);
    const booked = segs.filter((s) => isActive(s.event) && !isFocus(s.event));
    const loadMin = booked.reduce((total, s) => total + (s.endMin - s.startMin), 0);
    meetings += booked.length;
    bookedMin += loadMin;
    clashTotal += clashes.length;
    return {
      day,
      bars: segs.map((s) => ({ event: s.event, startMin: s.startMin, endMin: s.endMin, visual: eventVisual(s.event, clashing) })),
      loadMin,
      clashCount: clashes.length,
    };
  });

  const weekStart = dayStart(mon).getTime();
  const weekEnd = dayStart(addDays(mon, 7)).getTime();
  const inWeek = events.filter((e) => e.start.getTime() < weekEnd && e.end.getTime() > weekStart);
  const awaiting = inWeek
    .filter(needsReply)
    .sort((a, b) => a.start.getTime() - b.start.getTime())
    .map((event) => ({
      event,
      clashes: events.some(
        (o) =>
          o.id !== event.id &&
          !o.isAllDay &&
          isActive(o) &&
          !needsReply(o) &&
          o.start.getTime() < event.end.getTime() &&
          event.start.getTime() < o.end.getTime(),
      ),
    }));

  const weekendCount = [5, 6].reduce((total, i) => {
    const day = addDays(mon, i);
    const timed = timedSegments(events, day).filter((s) => isActive(s.event)).length;
    const allDay = allDayEvents(events, day).filter(isActive).length;
    return total + timed + allDay;
  }, 0);

  return {
    days,
    range: { startMin, endMin },
    stats: { meetings, bookedMin, clashes: clashTotal, toReply: awaiting.length },
    firstClashDay: days.find((d) => d.clashCount > 0)?.day ?? null,
    awaiting,
    weekendCount,
  };
}
